import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PoolPurposeBadge } from "@/components/pool-purpose-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPreemptionDuration, formatPreemptionTimestamp } from "@/lib/preemptions";
import type { PreemptionsResponse, SpotStatisticsResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

function number(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function percentage(delivered: number, requested: number): string {
  return requested > 0 ? `${number((delivered / requested) * 100)}%` : "—";
}

function Statistic({
  title,
  value,
  detail,
  hint,
}: {
  title: string;
  value: string;
  detail: string;
  hint?: string;
}) {
  return (
    <Card size="sm">
      <CardContent>
        <div className="text-xs text-muted-foreground" title={hint}>
          {title}
        </div>
        <div className="my-2 font-mono text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-[11px] leading-relaxed text-muted-foreground">{detail}</div>
      </CardContent>
    </Card>
  );
}

type CapacityPoint = SpotStatisticsResponse["timeline"][number];

function CapacityTooltip({
  active,
  payload,
  gpu,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: CapacityPoint }>;
  gpu: boolean;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-2 font-medium">{formatPreemptionTimestamp(point.start)} (UTC)</div>
      <div>Average running: {number(gpu ? point.running_gpus : point.running)}</div>
      <div>Average requested: {number(gpu ? point.requested_gpus : point.requested)}</div>
      <div>Average starting: {number(gpu ? point.starting_gpus : point.starting)}</div>
      <div className="mt-1 text-muted-foreground">
        {formatPreemptionDuration(point.observed_hours)} recorded
      </div>
    </div>
  );
}

export function SpotCapacityOverview({
  data,
  reliability,
}: {
  data: SpotStatisticsResponse;
  reliability?: PreemptionsResponse;
}) {
  const [gpu, setGpu] = useState(false);
  const period = data.period;
  const recorded = period.observed_hours > 0;
  const delivered = gpu ? period.gpu_hours : period.machine_hours;
  const requested = gpu ? period.requested_gpu_hours : period.requested_machine_hours;
  const fulfilled = gpu ? period.fulfilled_gpu_hours : period.fulfilled_machine_hours;
  const unit = gpu ? "GPU" : "machine";
  return (
    <div className="space-y-5">
      <section aria-label="Current spot capacity" className="rounded-xl border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="text-sm font-semibold">Now</h2>
            <span className="text-[11px] text-muted-foreground">
              Fleet-managed spot pools · {number(data.current.pools)} pools
            </span>
          </div>
          <Link
            to="/pools"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            View pools <ArrowUpRight className="size-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 divide-x lg:grid-cols-4">
          {[
            {
              title: "Running",
              value: data.current.running,
              detail: `${number(data.current.running_gpus)} GPUs · ${number(data.current.starting)} starting`,
            },
            {
              title: "Free",
              value: data.current.free,
              detail: "Running, unallocated and unlocked",
            },
            {
              title: "Requested",
              value: data.current.requested,
              detail: "Machines requested by active pools",
            },
            {
              title: "Shortfall",
              value: data.current.shortfall,
              detail: "Machines still needed across pools",
            },
          ].map((statistic) => (
            <div key={statistic.title} className="px-4 py-3">
              <div className="text-xs text-muted-foreground">{statistic.title}</div>
              <div
                className={cn(
                  "mt-1 font-mono text-2xl font-semibold tabular-nums",
                  statistic.title === "Shortfall" &&
                    statistic.value > 0 &&
                    "text-amber-600 dark:text-amber-400",
                )}
              >
                {number(statistic.value)}
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">{statistic.detail}</div>
            </div>
          ))}
        </div>
        <div className="border-t px-4 py-2 text-[10px] text-muted-foreground">
          Inventory read {formatPreemptionTimestamp(data.current.observed_at)} (UTC) · Current
          capacity is independent of the selected dates.
        </div>
      </section>

      <section aria-label="Selected period delivery" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">During the selected period</h2>
          <fieldset
            aria-label="Capacity unit"
            className="flex gap-1 rounded-lg border bg-card p-0.5"
          >
            {[false, true].map((option) => (
              <Button
                key={String(option)}
                size="sm"
                variant={gpu === option ? "secondary" : "ghost"}
                aria-pressed={gpu === option}
                onClick={() => setGpu(option)}
              >
                {option ? "GPUs" : "Machines"}
              </Button>
            ))}
          </fieldset>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Statistic
            title="Capacity delivered"
            value={number(recorded ? delivered : null)}
            detail={
              recorded
                ? `${unit}-hours · ${number(gpu ? period.machine_hours : period.gpu_hours)} ${gpu ? "machine" : "GPU"}-hours`
                : "History is collecting"
            }
            hint="Observed running capacity, including machines running outside their pool schedule."
          />
          <Statistic
            title="Average running"
            value={number(recorded ? delivered / period.observed_hours : null)}
            detail={`${gpu ? "GPUs" : "Machines"} over recorded time`}
          />
          <Statistic
            title="Target fulfilled"
            value={percentage(fulfilled, requested)}
            detail={
              requested > 0
                ? `${number(fulfilled)} of ${number(requested)} requested ${unit}-hours`
                : recorded
                  ? "No scheduled demand"
                  : "History is collecting"
            }
            hint="Delivery is capped at each pool's target before summing. Surplus in one pool cannot cover another pool's shortage."
          />
          <Statistic
            title="Median before interruption"
            value={formatPreemptionDuration(reliability?.summary.median_preempted_lifetime_hours)}
            detail={`From ${number(reliability?.summary.known_lifetime_count ?? 0)} interruptions with known starts`}
            hint="All recorded spot machines. Completed, provider-confirmed interruptions only; unfinished runs are excluded."
          />
        </div>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Capacity over time</CardTitle>
          <CardDescription className="text-xs">
            Running {gpu ? "GPUs" : "machines"} compared with the pool targets active at the time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recorded ? (
            <>
              <div className="mb-3 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-3 rounded-sm bg-primary" />
                  Running
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 border-t-2 border-dashed border-amber-500" />
                  Requested
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 border-t border-sky-500" />
                  Starting
                </span>
              </div>
              <ResponsiveContainer width="100%" height={270}>
                <ComposedChart
                  data={data.timeline}
                  margin={{ top: 8, right: 12, left: -16, bottom: 4 }}
                  accessibilityLayer
                >
                  <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="start"
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={35}
                    tickFormatter={(value: string) =>
                      new Date(value).toLocaleString("en-US", {
                        timeZone: "UTC",
                        month: "short",
                        day: "numeric",
                        ...(data.timeline.length > 36
                          ? {}
                          : { hour: "2-digit", hourCycle: "h23" as const }),
                      })
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CapacityTooltip gpu={gpu} />} />
                  <Area
                    type="stepAfter"
                    dataKey={gpu ? "running_gpus" : "running"}
                    fill="var(--primary)"
                    fillOpacity={0.15}
                    stroke="var(--primary)"
                    strokeWidth={2}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="stepAfter"
                    dataKey={gpu ? "requested_gpus" : "requested"}
                    stroke="var(--color-amber-500)"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="stepAfter"
                    dataKey={gpu ? "starting_gpus" : "starting"}
                    stroke="var(--color-sky-500)"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-center">
              <p className="text-sm font-medium">History is collecting</p>
              <p className="max-w-md text-xs text-muted-foreground">
                Current capacity is available above. Delivery and target coverage appear after
                consecutive observations.
              </p>
            </div>
          )}
          <div className="mt-3 text-[11px] text-muted-foreground">
            {formatPreemptionDuration(period.observed_hours)} recorded in this window
            {data.coverage_started_at
              ? ` · Collection began ${formatPreemptionTimestamp(data.coverage_started_at)} (UTC)`
              : ""}
            . Bucket averages use recorded time; gaps are excluded.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pool delivery</CardTitle>
          <CardDescription className="text-xs">
            Where requested capacity was missed, with each pool's current state alongside.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">Pool</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead className="text-right">Target fulfilled</TableHead>
                <TableHead className="text-right">Missed {unit}-hours</TableHead>
                <TableHead className="text-right">Time at target</TableHead>
                <TableHead className="text-right">Now</TableHead>
                <TableHead className="pr-4">Status now</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.pools.map((pool) => {
                const requestedHours = gpu
                  ? pool.period.requested_gpu_hours
                  : pool.period.requested_machine_hours;
                const fulfilledHours = gpu
                  ? pool.period.fulfilled_gpu_hours
                  : pool.period.fulfilled_machine_hours;
                const targetRatio = requestedHours ? fulfilledHours / requestedHours : null;
                return (
                  <TableRow key={`${pool.pool_id}/${pool.pool_created_at}`}>
                    <TableCell className="pl-4">
                      <div className="font-medium">
                        {pool.current ? (
                          <Link to="/pools" className="hover:text-primary hover:underline">
                            {pool.label || `${pool.gpu_count}× ${pool.gpu_type.toUpperCase()}`}
                          </Link>
                        ) : (
                          pool.label || `${pool.gpu_count}× ${pool.gpu_type.toUpperCase()}`
                        )}
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        {pool.provider} · {pool.gpu_count}× {pool.gpu_type.toUpperCase()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PoolPurposeBadge purpose={pool.purpose} />
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {percentage(fulfilledHours, requestedHours)}
                      {targetRatio != null && (
                        <div className="mt-1.5 ml-auto h-1 w-20 overflow-hidden rounded-full bg-muted">
                          <div
                            className={cn(
                              "h-full",
                              targetRatio >= 1 ? "bg-primary" : "bg-amber-500",
                            )}
                            style={{ width: `${Math.min(100, targetRatio * 100)}%` }}
                          />
                        </div>
                      )}
                      {requestedHours === 0 && (
                        <div className="mt-1 font-sans text-[10px] text-muted-foreground">
                          {pool.period.observed_hours ? "No scheduled demand" : "No history"}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {number(pool.period.observed_hours ? requestedHours - fulfilledHours : null)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {percentage(pool.period.at_target_hours, pool.period.requested_hours)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">
                      {pool.current ? `${pool.current.running} / ${pool.current.requested}` : "—"}
                      {pool.current && pool.current.starting > 0 && (
                        <div className="mt-1 font-sans text-[10px] text-muted-foreground">
                          {pool.current.starting} starting
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-60 pr-4 text-xs">
                      <span
                        className={cn(
                          pool.current &&
                            pool.current.running < pool.current.requested &&
                            "text-amber-600 dark:text-amber-400",
                        )}
                      >
                        {pool.current?.status ?? "Deleted"}
                      </span>
                      {pool.current?.reason && (
                        <div
                          className="mt-1 max-w-60 truncate text-[10px] text-muted-foreground"
                          title={pool.current.reason}
                        >
                          {pool.current.reason}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {data.pools.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No spot pools match these filters.{" "}
                    <Link to="/pools" className="text-primary hover:underline">
                      Manage pools
                    </Link>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <p className="px-4 pt-3 text-[10px] text-muted-foreground">
            Delivery uses historical targets and running capacity. Time at target counts only
            scheduled demand. Now shows running / currently requested machines.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function SpotLifetimeOverview({ data }: { data: PreemptionsResponse }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How long spots run</CardTitle>
        <CardDescription className="text-xs">
          All recorded spot machines. Completed lifetimes and unfinished runs are shown separately.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div>
          <h3 className="mb-3 text-xs font-medium">Runtime before confirmed interruption</h3>
          {data.summary.known_lifetime_count > 0 ? (
            <ResponsiveContainer width="100%" height={170}>
              <BarChart
                data={data.lifetimes.distribution}
                margin={{ top: 4, right: 4, left: -24, bottom: 0 }}
                accessibilityLayer
              >
                <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [value, "Completed runs"]}
                  contentStyle={{
                    background: "var(--popover)",
                    borderColor: "var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="var(--primary)"
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-40 items-center justify-center text-xs text-muted-foreground">
              No confirmed interruptions with known starts in this window.
            </div>
          )}
          <p className="mt-2 text-[10px] text-muted-foreground">
            {number(data.summary.known_lifetime_count)} known-start interruptions ·{" "}
            {number(data.summary.unknown_start_count)} observed runs have unknown starts.
          </p>
        </div>
        <div className="rounded-lg border bg-muted/20 p-4">
          <h3 className="text-xs font-medium">Unfinished runs</h3>
          <div className="mt-3 font-mono text-3xl font-semibold">
            {number(data.lifetimes.unfinished_runs)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            had no recorded end by the period end
          </div>
          <div className="mt-5 text-xs text-muted-foreground">Median age at last observation</div>
          <div className="mt-1 font-mono text-xl font-medium">
            {formatPreemptionDuration(data.lifetimes.median_unfinished_age_hours)}
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
            {number(data.lifetimes.known_unfinished_count)} with known starts. These are still
            unfinished observations, not completed lifetimes or a prediction.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
